import { Link } from 'react-router-dom'
import { useEffect, useRef, type ReactNode } from 'react'
import { testimonials } from '@/data/journeys'
import { Button } from '@/components/ui'
import { SEO, HomePageStructuredData } from '@/components/seo'
import { usePricingTiers } from '@/hooks/usePricingTiers'
import { useUtmPassthrough } from '@/hooks/useUtmPassthrough'
import { appSignupLink } from '@/utils/signupUrl'
import { trialCopy } from '@/utils/trialCopy'
import { NEW_PRICING_ENABLED } from '@/utils/featureFlags'

const HERO_LINEAR_GRADIENT =
  'linear-gradient(135deg, #0f766e 0%, #0f172a 50%, #065f46 100%)'
const HERO_RADIAL_OVERLAY =
  'radial-gradient(circle at 30% 40%, rgba(20,184,166,0.15) 0%, transparent 60%), radial-gradient(circle at 70% 60%, rgba(5,150,105,0.10) 0%, transparent 50%)'

function HeroBackdrop() {
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Dot grid: subtle precision */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.35]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="hero-dot-grid" width="34" height="34" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.8" fill="rgba(94,234,212,0.18)" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hero-dot-grid)" />
      </svg>

      {/* Soft glow orbs: atmospheric depth, gently drifting */}
      <div
        className="absolute -left-40 top-10 w-[520px] h-[520px] rounded-full motion-safe:animate-drift-1 will-change-transform"
        style={{
          background: 'radial-gradient(circle, rgba(20,184,166,0.22) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <div
        className="absolute -right-32 bottom-0 w-[640px] h-[640px] rounded-full motion-safe:animate-drift-2 will-change-transform"
        style={{
          background: 'radial-gradient(circle, rgba(5,150,105,0.28) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />
      <div
        className="absolute left-1/3 -top-20 w-[420px] h-[420px] rounded-full motion-safe:animate-drift-3 will-change-transform"
        style={{
          background: 'radial-gradient(circle, rgba(94,234,212,0.10) 0%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />

      {/* Flowing wave lines: echo the dino-egg silhouette, slow horizontal drift */}
      <svg
        className="absolute inset-0 w-full h-full motion-safe:animate-wave-drift"
        viewBox="0 0 1200 800"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M-50 380 Q 280 250, 600 360 T 1250 340"
          stroke="rgba(94,234,212,0.10)"
          strokeWidth="1.2"
          fill="none"
        />
        <path
          d="M-50 520 Q 340 430, 700 520 T 1250 500"
          stroke="rgba(20,184,166,0.07)"
          strokeWidth="1"
          fill="none"
        />
        <path
          d="M-50 660 Q 360 590, 720 660 T 1250 630"
          stroke="rgba(184,149,106,0.06)"
          strokeWidth="0.8"
          fill="none"
        />
      </svg>
    </div>
  )
}

function HeroProductShot() {
  return (
    <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/15 bg-slate-900">
      {/* browser chrome */}
      <div className="flex items-center gap-1.5 px-4 py-3 bg-slate-800/80 border-b border-white/10">
        <span className="w-3 h-3 rounded-full bg-white/20" />
        <span className="w-3 h-3 rounded-full bg-white/20" />
        <span className="w-3 h-3 rounded-full bg-white/20" />
        <span className="ml-3 text-xs text-white/50 truncate">app.wealthspace.co.uk</span>
      </div>
      <img
        src="/hero-product.png"
        alt="The WealthSpace app welcome screen: choose your adviser experience"
        className="block w-full h-auto"
        loading="eager"
      />
    </div>
  )
}

function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('opacity-100', 'translate-y-0')
          el.classList.remove('opacity-0', 'translate-y-6')
          observer.unobserve(el)
        }
      },
      { threshold: 0.15 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return (
    <div ref={ref} className={`opacity-0 translate-y-6 transition-all duration-700 ease-out ${className}`}>
      {children}
    </div>
  )
}

export default function HomePage() {
  const { tiers } = usePricingTiers()
  const utm = useUtmPassthrough()
  const signup = appSignupLink({ params: utm })
  const entryPrice = tiers
    .map((t) => t.monthlyPrice)
    .filter((p): p is number => typeof p === 'number')
    .sort((a, b) => a - b)[0]
  const fromPriceLabel = entryPrice ? `From £${entryPrice}/adviser/month` : null

  return (
    <>
      <SEO
        title="WealthSpace | The AI Toolkit You Can Actually Try | UK Financial Advisers"
        description={`AI for UK financial advisers, no hype. Record the meeting, draft the report, and run every client in one place.${fromPriceLabel ? ` ${fromPriceLabel}.` : ''} ${trialCopy.shortLabel}.`}
        keywords="financial adviser software UK, AI suitability reports, practice management software, IFA software, self-serve adviser platform, no contract adviser software"
        url="/"
      />
      <HomePageStructuredData />

      {/* 1. Hero: full viewport */}
      <section
        className={`min-h-[calc(100vh-80px)] flex items-center px-6 py-20 relative overflow-hidden ${
          NEW_PRICING_ENABLED ? 'text-white' : ''
        }`}
        style={NEW_PRICING_ENABLED ? { background: HERO_LINEAR_GRADIENT } : undefined}
      >
        {NEW_PRICING_ENABLED && (
          <>
            <HeroBackdrop />
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{ background: HERO_RADIAL_OVERLAY }}
            />
          </>
        )}
        <div className="max-w-[1200px] mx-auto w-full relative z-[1]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p
                className={`text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-6 opacity-0 animate-fade-in-up [animation-delay:0.1s] ${
                  NEW_PRICING_ENABLED ? 'text-teal-200/80' : 'text-accent-teal'
                }`}
              >
                Built by Advisers · Run by Advisers
              </p>
              <h1
                className={`font-serif text-[clamp(40px,5.5vw,60px)] font-normal leading-[1.02] tracking-[-0.02em] mb-7 opacity-0 animate-fade-in-up [animation-delay:0.15s] ${
                  NEW_PRICING_ENABLED ? 'text-white' : 'text-slate-900'
                }`}
              >
                No bullsh*t.
                <br />
                <span className={NEW_PRICING_ENABLED ? 'text-teal-200 italic' : 'text-accent-teal'}>
                  Just get on with advice.
                </span>
              </h1>

              <p
                className={`text-lg sm:text-xl leading-[1.5] max-w-[540px] mb-8 opacity-0 animate-fade-in-up [animation-delay:0.2s] ${
                  NEW_PRICING_ENABLED ? 'text-white/70' : 'text-slate-600'
                }`}
              >
                Record the meeting, draft the report, run every client, all in one
                place. WealthSpace handles the admin.
              </p>

              <div className="opacity-0 animate-fade-in-up [animation-delay:0.25s]">
                <Button href={signup.href} external={signup.external} size="lg">
                  Start Free Trial
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>

              <p
                className={`text-sm mt-5 opacity-0 animate-fade-in-up [animation-delay:0.3s] ${
                  NEW_PRICING_ENABLED ? 'text-white/55' : 'text-slate-500'
                }`}
              >
                {trialCopy.shortLabel} · Cancel anytime
                {fromPriceLabel ? ` · ${fromPriceLabel}` : ''}
              </p>
            </div>

            {/* Product preview */}
            <div className="hidden lg:block opacity-0 animate-fade-in-up [animation-delay:0.35s]">
              <HeroProductShot />
            </div>
          </div>
        </div>
      </section>

      {/* 2. Comparison: the USP */}
      <Reveal>
        <section className="py-28 px-6 bg-slate-900 text-white">
          <div className="max-w-[1000px] mx-auto">
            <p className="text-center text-sm text-slate-400 uppercase tracking-wider mb-14">How other tools work vs how WealthSpace works</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-slate-800 rounded-xl p-10">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-6">The usual process</div>
                <ul className="space-y-4">
                  {[
                    'Book a demo with a sales rep',
                    'Wait for a proposal',
                    'Sign an annual contract',
                    '6-week implementation project',
                    'Training sessions for your team',
                    'Hope it actually works',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[15px] text-slate-400">
                      <svg className="w-4 h-4 text-slate-600 flex-shrink-0 mt-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-teal-900/50 to-slate-800 border border-teal-700/30 rounded-xl p-10">
                <div className="text-xs font-semibold text-teal-400 uppercase tracking-wider mb-6">The WealthSpace way</div>
                <ul className="space-y-4">
                  {[
                    NEW_PRICING_ENABLED ? 'Sign up in 60 seconds, log straight in' : 'Sign up online in 2 minutes',
                    NEW_PRICING_ENABLED ? 'Card-secured 7-day trial · auto-converts to paid' : 'Start using it immediately',
                    'Monthly rolling, cancel anytime',
                    'No implementation needed',
                    "Built so simple you won't need training",
                    'Not for you? Cancel with one click',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[15px] text-slate-200">
                      <svg className="w-4 h-4 text-teal-400 flex-shrink-0 mt-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      </Reveal>

      {/* 3. What's included */}
      <section className="py-28 px-6">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-sm font-semibold text-accent-teal tracking-[0.2em] uppercase mb-3">
                One platform · four modules
              </p>
              <h2 className="font-serif text-[clamp(28px,4vw,40px)] text-slate-900 mb-4">
                Everything an advice firm runs on
              </h2>
              <p className="text-lg text-slate-600 max-w-[560px] mx-auto">
                Recordings, reports, the client suite, and the day-to-day tooling, built around how UK advisers actually work.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              {
                name: 'AI Reports',
                tagline: 'Suitability reports that used to take 3 hours, done in minutes',
                features: ['FCA-ready suitability letters', 'Drafted from your meeting notes', 'Your templates, your tone'],
                icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8',
              },
              {
                name: 'Meeting Capture',
                tagline: 'Record the meeting. Get the notes, actions, and file note automatically',
                features: ['Live transcription', 'AI-generated file notes', 'Apple Watch & CarPlay dictation'],
                icon: 'M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z M19 10v2a7 7 0 0 1-14 0v-2 M12 19v4 M8 23h8',
              },
              {
                name: 'Client Portal',
                tagline: 'Coming soon: your clients see their wealth, documents, and progress, 24/7',
                features: ['Portfolio and document access', 'Secure messaging', 'Branded to your firm'],
                icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 7a4 4 0 1 0 0-0.01 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
              },
              {
                name: 'Practice Tools',
                tagline: 'Tasks, VoIP, the boring stuff sorted',
                features: ['Task management', 'Built-in call recording', 'Team channels'],
                icon: 'M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z',
              },
            ].map((mod) => (
              <Reveal key={mod.name}>
                <div className="bg-white border border-slate-200 rounded-xl p-10 shadow-card h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover hover:border-slate-300">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-xl bg-teal-50 flex items-center justify-center text-accent-teal flex-shrink-0">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d={mod.icon} />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-serif font-medium text-slate-900 mb-1">{mod.name}</h3>
                      <p className="text-slate-600 text-[15px] mb-4">{mod.tagline}</p>
                      <ul className="space-y-2">
                        {mod.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-slate-500">
                            <svg className="w-4 h-4 text-accent-teal flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="text-center mt-14">
              <Button href="/product" variant="outline" size="sm">
                See all features
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4. Social proof */}
      <section className="py-28 px-6 bg-slate-50">
        <div className="max-w-[1000px] mx-auto">
          <Reveal>
            <h2 className="font-serif text-[clamp(28px,4vw,40px)] text-slate-900 text-center mb-16">
              What advisers say
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.slice(0, 2).map((t, i) => (
              <Reveal key={i}>
                <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-card h-full">
                  <div className="text-amber-400 mb-4 tracking-[2px] text-lg">★★★★★</div>
                  <p className="text-[16px] text-slate-700 leading-[1.8] mb-6 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div>
                    <div className="text-sm text-slate-900 font-semibold">{t.author}</div>
                    <div className="text-[13px] text-accent-teal">
                      {t.role}{t.company && `, ${t.company}`}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4b. Reassurance band: privately adviser-owned */}
      <Reveal>
        <section className="py-20 px-6 bg-white border-y border-slate-200">
          <div className="max-w-[700px] mx-auto text-center">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-accent-teal mb-5">
              Privately adviser-owned
            </p>
            <h2 className="font-serif text-[clamp(28px,4vw,40px)] text-slate-900 leading-[1.15] mb-5">
              2.5 years in. <span className="text-accent-teal italic">Not going anywhere.</span>
            </h2>
            <p className="text-slate-600 max-w-[520px] mx-auto leading-relaxed">
              No VC. No flip. No annual price hike to fund a growth round. Built by advisers, run by advisers, owned by advisers, and around long enough to prove it.
            </p>
          </div>
        </section>
      </Reveal>

      {/* 5. Free resources */}
      <section className="py-28 px-6 bg-slate-900 text-white">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-sm font-semibold text-teal-400 tracking-wide uppercase mb-4">No sign-up required</p>
              <h2 className="font-serif text-[clamp(28px,4vw,40px)] text-white mb-4">
                Free tools for any adviser
              </h2>
              <p className="text-lg text-slate-400 max-w-[480px] mx-auto">
                Use these whether you're a customer or not. Seriously.
              </p>
            </div>
          </Reveal>
          <Reveal>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { href: '/ai-buyers-checklist', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', title: "AI Buyer's Checklist", desc: '10 questions to ask any vendor' },
                { href: '/value-of-advice', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', title: 'Value of Advice Calculator', desc: 'Show clients what advice is worth' },
                { href: '/demo', icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z', title: 'Interactive Demo', desc: 'Try the product, no sign-up' },
                { href: '/case-studies', icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2', title: 'Case Studies', desc: 'Illustrative adviser scenarios' },
                { href: '/security', icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', title: 'Security & Compliance', desc: 'How we protect your data' },
              ].map((r) => (
                <Link key={r.href} to={r.href} className="group flex flex-col items-center text-center p-6 rounded-xl bg-slate-800/80 border border-white/10 no-underline transition-all duration-300 hover:border-teal-400/40 hover:bg-slate-800 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(20,184,166,0.15)]">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/15 border border-teal-400/30 flex items-center justify-center mb-4 group-hover:bg-teal-500/25 transition-colors">
                    <svg className="w-6 h-6 text-teal-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={r.icon} /></svg>
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-1">{r.title}</h3>
                  <p className="text-xs text-slate-300 leading-relaxed">{r.desc}</p>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. Final CTA */}
      <Reveal>
        <section className="py-32 px-6">
          <div className="max-w-[600px] mx-auto text-center">
            <h2 className="font-serif text-[clamp(28px,4vw,40px)] text-slate-900 mb-6">
              Dip your toe in.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-10">
              {trialCopy.oneLine}.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5">
              <Button href={signup.href} external={signup.external} size="lg">
                Start Free Trial
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Button>
              <Button href="/demo" variant="outline" size="lg">
                Try the Demo First
              </Button>
            </div>
            <p className="text-sm text-slate-400">
              Adviser-owned. Privately funded. No strings attached.
            </p>
          </div>
        </section>
      </Reveal>
    </>
  )
}
